import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit'
import type {
  BannedKeywordStateType,
  BannedKeywordType,
  FetchBannedKeywordListPayloadType,
} from 'features/Account/@types/bannedKeyword'
import { callErrorMsg } from 'helpers/errorMsg'
import { toastSuccess } from 'utils/toast'
import {
  deleteBannedKeyword,
  getBannedKeywordList,
  postBannedKeyword,
  putBannedKeyword,
} from '../../services/bannedKeyword'

const initialState: BannedKeywordStateType = {
  isLoading: false,
  isSubmitting: false,
  requestId: '',
  keywords: [],
  nextCursor: '',
  previousCursor: '',
}

export const SLICE_NAME = 'bannedKeyword'

export const fetchBannedKeywordList = createAsyncThunk(
  `${SLICE_NAME}/fetchBannedKeywordList`,
  async (
    { search, limit, cursor, direction }: FetchBannedKeywordListPayloadType,
    { rejectWithValue },
  ) => {
    try {
      const res = await getBannedKeywordList({
        params: {
          limit,
          direction,
          cursor: cursor || undefined,
          search: search || undefined,
        },
      })
      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const createBannedKeyword = createAsyncThunk(
  `${SLICE_NAME}/createBannedKeyword`,
  async (keyword: string, { rejectWithValue }) => {
    try {
      await postBannedKeyword(keyword)
      toastSuccess('Keyword berhasil ditambahkan')
      return keyword
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const updateBannedKeyword = createAsyncThunk(
  `${SLICE_NAME}/updateBannedKeyword`,
  async ({ id, keyword }: Omit<BannedKeywordType, 'created_at'>, { rejectWithValue }) => {
    try {
      await putBannedKeyword({ id, keyword })
      toastSuccess('Keyword berhasil diubah')
      return id
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const removeBannedKeyword = createAsyncThunk(
  `${SLICE_NAME}/removeBannedKeyword`,
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteBannedKeyword(id)
      toastSuccess('Keyword berhasil dihapus')
      return id
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

const bannedKeywordSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(fetchBannedKeywordList.pending, (state, action) => {
        state.requestId = action.meta.requestId
        state.isLoading = true
      })
      .addCase(fetchBannedKeywordList.rejected, (state, action) => {
        // a newer request has replaced this one
        if (state.requestId !== action.meta.requestId) return

        state.isLoading = false
      })
      .addCase(fetchBannedKeywordList.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return

        const { data, pagination } = action.payload
        state.isLoading = false
        state.keywords = (data || []).map((item) => item.data)
        state.nextCursor = pagination.next_page_exist ? pagination.next_cursor : ''
        state.previousCursor = pagination.previous_page_exist ? pagination.previous_cursor : ''
      })
      .addMatcher(
        isAnyOf(
          createBannedKeyword.pending,
          updateBannedKeyword.pending,
          removeBannedKeyword.pending,
        ),
        (state) => {
          state.isSubmitting = true
        },
      )
      .addMatcher(
        isAnyOf(
          createBannedKeyword.fulfilled,
          createBannedKeyword.rejected,
          updateBannedKeyword.fulfilled,
          updateBannedKeyword.rejected,
          removeBannedKeyword.fulfilled,
          removeBannedKeyword.rejected,
        ),
        (state) => {
          state.isSubmitting = false
        },
      )
  },
})

export default bannedKeywordSlice.reducer
export const { reset } = bannedKeywordSlice.actions
